import {useConfig} from '@aboutyou/config';
import {
  NextActionsSection,
  NextActionsSection_ChatBotContact,
  NextActionsSection_ContactOption,
  NextActionsSection_DirectContact,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/order_success_page/v1/sections.types';
import {useTrackFeature} from '@aboutyou/tracking';
import {
  Button,
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  IconName,
  Image,
  Text,
  desktopAndUpLinaria,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useHelpCenterChatbot} from '../../HelpCenterChatbot/hooks/useHelpCenterChatbot';

export const NextActionContactSection = (props: NextActionsSection) => {
  const trackFeature = useTrackFeature();

  const trackGoToOrders = () => {
    trackFeature({
      eventType: 'click',
      label: 'to_orders_CTA',
      name: 'to_orders_CTA',
    });
  };

  const trackContinueShopping = () => {
    trackFeature({
      eventType: 'click',
      label: 'continue_shopping',
      name: 'continue_shopping',
    });
  };

  return (
    <NextActionContactStreamWrapper>
      {props.type && <ContactSection {...props.type} />}
      <NextActionWrapper data-testid="ospCTASectionContainer">
        <Button
          href="/"
          data-testid="continueShoppingCTA"
          onClick={trackContinueShopping}
          size="full-width"
        >
          {props.continueShoppingLabel}
        </Button>
        <Button
          variant="secondary"
          href="/a/orders"
          data-testid="goToOrdersCTA"
          onClick={trackGoToOrders}
        >
          {props.goToOrdersLabel}
        </Button>
      </NextActionWrapper>
    </NextActionContactStreamWrapper>
  );
};

const ContactSection = (props: NextActionsSection['type']) => {
  if (props?.$case === 'directContact') {
    return <DirectContactSection {...props.directContact} />;
  }

  if (props?.$case === 'chatBotContact') {
    return <ChatbotSection {...props.chatBotContact} />;
  }

  return null;
};

const DirectContactSection = (props: NextActionsSection_DirectContact) => {
  return (
    <ContactWrapper data-testid="contactSection">
      <SectionHeadline>{props.sectionHeadline}</SectionHeadline>
      <DirectContact>
        <ContactOptionsList data-testid="directContactOptionsList">
          {props.contactOptions.map((option, index) => (
            <ContactOption key={index} {...option} />
          ))}
        </ContactOptionsList>
        <ConditionsWrapper>
          {props.conditions.map((condition, index) => (
            <ConditionsText size="px12" color="Grey0" key={index}>
              {condition}
            </ConditionsText>
          ))}
        </ConditionsWrapper>
      </DirectContact>
    </ContactWrapper>
  );
};

const ChatbotSection = (props: NextActionsSection_ChatBotContact) => {
  const {open: openHelpCenterChatbot, isOpening} = useHelpCenterChatbot();

  const handleChatbotClick = () => {
    openHelpCenterChatbot();
  };

  return (
    <ContactWrapper data-testid="contactSection">
      <SectionHeadline>{props.sectionHeadline}</SectionHeadline>
      <ChatbotWrapper data-testid="chatbotSection">
        <TextAndIcon text={props.headline} icon={GenericIconName.Chatbot} />
        <Text color="Grey0">{props.subline}</Text>
        <ChatbotButton
          variant="secondary"
          isLoading={isOpening}
          onClick={handleChatbotClick}
        >
          {props.ctaLabel}
        </ChatbotButton>
      </ChatbotWrapper>
    </ContactWrapper>
  );
};

const TextAndIcon = (props: {text: string; icon: IconName}) => {
  return (
    <TextAndIconContainer>
      <Icon asset={props.icon} size="M" />
      <StyledText size="px18" weight="mediumHeavy">
        {props.text}
      </StyledText>
    </TextAndIconContainer>
  );
};

const ContactOption = (props: NextActionsSection_ContactOption) => {
  const {device} = useConfig();

  const handleClick = () => {
    if (props.link?.url) {
      window.location.href = props.link.url;
    }
  };

  // we dont want telephone numbers on desktop to be clickable
  const clickable =
    (props.link && !props.link.url.includes('tel:')) || device === 'mobile';

  return (
    <ContactOptionWrapper
      onClick={handleClick}
      $clickable={clickable}
      data-testid="contactOption"
    >
      <div>
        <ContactOptionHeadlineWrapper>
          <ContactOptionImage
            src={props.icon.url}
            widths={[50]}
            breakpoints={[]}
            ratio="1:1"
          />
          <Text weight="mediumHeavy" size="px16">
            {props.title}
          </Text>
        </ContactOptionHeadlineWrapper>
        <ContactOptionSubline size="px14" weight="mediumHeavy" color="Grey0">
          {props.subtitle}
        </ContactOptionSubline>
      </div>
      {clickable && <ContactIcon asset={GenericIconName.ArrowRight} size="S" />}
    </ContactOptionWrapper>
  );
};

const SECTION_HEADLINE_LINE_HEIGHT = '1.16';

const CONTACT_SECTION_GAP = '1.5rem';

const ContactIcon = styled(Icon)`
  cursor: pointer;
`;

const ConditionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ContactOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  ${desktopAndUpLinaria(`
    flex-direction: row;
  `)}
`;

const ContactOptionWrapper = styled.div<{
  $clickable: boolean;
}>`
  padding: 1rem;
  display: flex;
  gap: 1rem;
  border-radius: 0.25rem;
  border: 1px solid ${COLORS.Grey3};
  justify-content: space-between;
  align-items: center;
  flex: 1;

  cursor: ${props => (props.$clickable ? 'pointer' : 'unset')};

  ${desktopAndUpLinaria(`
    max-width: 50%;
  `)}
`;

const ContactOptionImage = styled(Image)`
  width: 1.75rem;
  height: 1.75rem;
`;

const ContactOptionHeadlineWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  line-height: 1.44;
`;

const ContactOptionSubline = styled(Text)`
  line-height: 1.5;
`;

const SectionHeadline = styled(Text)`
  text-align: center;
  font-size: ${FONT_SIZES.px18};
  line-height: 1.44; /* 144.444% */
  font-weight: ${FONT_WEIGHTS.mediumHeavy};

  ${tabletAndUpLinaria(`
    line-height: ${SECTION_HEADLINE_LINE_HEIGHT};
    text-align: unset;
  `)}
`;

const ChatbotWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  padding: 1.5rem;

  border-radius: 0.25rem;
  border: 1px solid ${COLORS.Grey3};

  & > * {
    flex: 1;
  }

  ${tabletAndUpLinaria(`
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `)};
`;

const ChatbotButton = styled(Button)`
  margin-top: 1rem;

  ${tabletAndUpLinaria(`
    margin-top: unset;
  `)}
`;

const ConditionsText = styled(Text)`
  line-height: 1.5;
`;

const TextAndIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StyledText = styled(Text)`
  line-height: 1.44;
`;

const ContactWrapper = styled.div`
  background-color: ${COLORS.White};
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: ${CONTACT_SECTION_GAP};

  ${tabletAndUpLinaria(`
    flex: 3;
    padding: 0;
  `)}
`;

const NextActionWrapper = styled.div`
  background-color: ${COLORS.White};
  padding: 1rem;
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
  ${tabletAndUpLinaria(`
    flex: 1;
    padding: 0;
  `)}
`;

export const NextActionContactStreamWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  background-color: ${COLORS.Grey4};
  gap: 0.5rem;

  ${tabletAndUpLinaria(`
    flex-direction: row;
    background-color: unset;
    justify-content: space-between;
    gap: 2.5rem;
    padding: 1.5rem 0 2rem 0;
  `)}
  // we need to add this margin to align it with the contact section, which is a sibling
  ${ContactWrapper} + ${NextActionWrapper} {
    ${tabletAndUpLinaria(`
      margin-top: calc(${SECTION_HEADLINE_LINE_HEIGHT} + ${CONTACT_SECTION_GAP});
    `)}
  }
`;

const DirectContact = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
